import React, { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Redux
import { useQueryParamsReducer } from '@/main/store/queryParams';

// Services
import { toast } from 'react-toastify';
import { getTranslateByKey, isNullUndefined } from '@/main/services/utils';

// Types
import { KeyValue } from '@/main/models/common';
import { DynamicReportRequest } from '@/main/models/request/dynamicReportRequest';
import { mapDynamicReportResponseToFormData, mapDynamicReportFormDataToRequest } from './utilRegisterDynamicReport';
import { DynamicReportHistoricalResponse, DynamicReportResponse } from '@/main/models/response';
import { RoutesConstants } from '@/main/routes/routesConstants';

// Api
import { procurementReportApi } from '@/main/api';


// Components
import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import GeneralDataSection from './components/generalDataSection/generalDataSection';
import QuerySection from './components/querySection/querySection';
import VariablesSection from './components/variablesSection/variablesSection';
import PermissionsSection from './components/permissionsSection/permissionsSection';
import HistoricSection from './components/historicSection/historicSection';


import * as S from './styles';
import Button from '@/main/components/shared/button/button';
import { CompanyTypeEnum } from '@/main/services/enum/CompanyTypeEnum';



export interface DynamicReportForm {
  guidDynamicReport: string | null;
  country: KeyValue | null;
  reportName: string;
  statusRegister: KeyValue | null;
  query: string;
  variables: DynamicReportVariableForm[];
  permissions: DynamicReportPermissionForm[];
}

export interface DynamicReportVariableForm {
  guidDynamicReportVariable: string | null;
  variableType:  KeyValue | null;
  variableName: string;
  variableRangeName: string;
  filterName: string;
  mandatory: boolean;
  ordering: number;
  maxAllowedRange: number | null;
}

export interface DynamicReportPermissionForm {
  guidDynamicReportPermission: string | null;
  companyType: KeyValue | null;
  company: KeyValue | null;
  allCompanies: boolean;
}

export function RegisterDynamicReport() {

  const navigate = useNavigate();
  const { queryParams: queryParamsReducer } = useQueryParamsReducer();
  const [searchParams, setSearchParams] = useSearchParams();
  const guidDynamicReportParam = searchParams.get('guidDynamicReport');

  const [isEdit, setIsEdit] = useState(!isNullUndefined(guidDynamicReportParam));
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState<boolean>(false);

  const INITIAL_FORM_STATE : DynamicReportForm = {
    guidDynamicReport: null,
    country: null,
    reportName: '',
    statusRegister: null,
    query:  '',
    variables: [],
    permissions: [],
  };

  const methods = useForm<DynamicReportForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE,
  });

  const {
    reset,
    register,
    handleSubmit,
    setValue
  } = methods;

  useEffect( () => {
    if (!isEdit) {
      const defaultSelfPermission: DynamicReportPermissionForm = {
        guidDynamicReportPermission: null,
        companyType: { key: CompanyTypeEnum.Admin, value: getTranslateByKey('companyType.admin')},
        company: null,
        allCompanies: true,
      }
      setValue('permissions', [defaultSelfPermission]);
    }
  }, []);

  function generateAtLeastOneValidator(fieldName: string){
    return (values: any[]) => {
      if( !values || values.length == 0){
        const error =  getTranslateByKey('information.atLeastOne', true)
          .replace('{0}', fieldName);
          toast.error(error);
          return error;
      }
      return true;
    }
  }

  register('variables', { validate: generateAtLeastOneValidator(getTranslateByKey('dynamicReport.variable'))});
  register('permissions', { validate: generateAtLeastOneValidator(getTranslateByKey('dynamicReport.permission'))});

  function fillExistingData(data: DynamicReportResponse) {
    const formData = mapDynamicReportResponseToFormData(data)
    reset(formData);
  }

  function changeScreenToEdit(guidDynamicReport: string) {
    setIsEdit(true);
    setSearchParams({ guidDynamicReport }, { replace: true });
  }

  function loadDynamicReport(guidDynamicReport: string) {
    setIsLoadingEdit(true);
    procurementReportApi.getDynamicReportDetails(guidDynamicReport)
      .then( (reponse) =>{
        reponse.data.result.guidDynamicReport = guidDynamicReport;
        fillExistingData(reponse.data.result);
      }).finally( () => setIsLoadingEdit(false) );

  }


  function getFormatedRequest(values: DynamicReportForm): DynamicReportRequest{
    return mapDynamicReportFormDataToRequest(values);
  }

  function save(values: DynamicReportForm){
    setIsLoadingSave(true);
    const request = getFormatedRequest(values);
    procurementReportApi
      .saveDynamicReport(request)
      .then( (response) => {
        const guidNewRecord = response.data.result;

        toast.success(
          getTranslateByKey('dynamicReport.savedSuccessfully')
        );

        if (!isEdit) return changeScreenToEdit(guidNewRecord);

        loadDynamicReport(guidNewRecord);

      }).finally( () => {setIsLoadingSave(false)});

  }

  function handleBack() {
    let redirectRoute = RoutesConstants.dynamicReport;

    if (!isNullUndefined(queryParamsReducer.data)) {
      const nameKeyQueryParams = RoutesConstants.dynamicReport;
      const queryParams = queryParamsReducer.data[nameKeyQueryParams];
      redirectRoute = queryParams ? `${RoutesConstants.dynamicReport}?${new URLSearchParams(queryParams)}` : RoutesConstants.dynamicReport
    }

    navigate(redirectRoute);
  }

  useEffect(() => {
    if (!isNullUndefined(guidDynamicReportParam)) {
      loadDynamicReport(guidDynamicReportParam);
    }
  }, [guidDynamicReportParam]);

  const titlePage = `${getTranslateByKey('module.report')} > ${getTranslateByKey('dynamicReport.register')}`;

  return (
    <ContainerScreen title={titlePage}>
      <S.Container>
        <FormProvider {...methods }>
          <form onSubmit={handleSubmit(save)} noValidate>
            <GeneralDataSection></GeneralDataSection>
            <QuerySection></QuerySection>
            <VariablesSection></VariablesSection>
            <PermissionsSection></PermissionsSection>
            {
              isEdit && guidDynamicReportParam &&
              <HistoricSection
                guidDynamicReport={guidDynamicReportParam}
                isLoadingEdit={isLoadingEdit}
              />
            }

            <S.AreaButton>
              <SecondaryButton
                id="id-registerDynamicReport-btn-back"
                type="button"
                onClick={handleBack}
              >
                {getTranslateByKey('information.back')}
              </SecondaryButton>
              <Button
                type="submit"
                isLoading={isLoadingSave || isLoadingEdit}
              >
                { isEdit ? getTranslateByKey('information.edit')  : getTranslateByKey('information.save') }
              </Button>
            </S.AreaButton>
          </form>
        </FormProvider>
      </S.Container>
    </ContainerScreen>
  );
}


export default RegisterDynamicReport;